import React, { useEffect, useMemo, useState } from 'react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AutoRajaLanding from './components/AutoRajaLanding';
import AutoRajaSupport from './components/AutoRajaSupport';
import AutoRajaPrivacy from './components/AutoRajaPrivacy';
import AutoRajaDeleteData from './components/AutoRajaDeleteData';
import AutoRajaCredits from './components/AutoRajaCredits';
import './App.css';

const SITE_ORIGIN = 'https://mixout.in';
const DEFAULT_OG_IMAGE = '/logos/marketing/logo_landscape_2400x1350_safe.png';

const ROUTE_META = {
  home: {
    title: 'Mixout Technologies Private Limited',
    description:
      'Meet AutoRaja, MacDelta, and Studio Carto: games, Mac utilities, and creative tools from Mixout Technologies Private Limited.',
    path: '/',
  },
  'autoraja-credits': {
    title: 'AutoRaja Credits & Licenses | MixOut Technologies',
    description:
      'Third-party software and assets credited for AutoRaja, a mobile game by Mixout Technologies Private Limited.',
    path: '/autoraja/credits',
  },
  autoraja: {
    title: 'AutoRaja | MixOut Technologies',
    description:
      'AutoRaja is a fast-paced auto-rickshaw driving game featuring traffic dodging, coins, hazards, music, and customization.',
    path: '/autoraja',
  },
  'autoraja-support': {
    title: 'AutoRaja Support | MixOut Technologies',
    description:
      'Get support for AutoRaja, including help with gameplay, ads, performance, bugs, and purchases.',
    path: '/autoraja/support',
  },
  'autoraja-privacy': {
    title: 'AutoRaja Privacy Policy | MixOut Technologies',
    description:
      'Read the privacy policy for AutoRaja by MixOut Technologies Private Limited.',
    path: '/autoraja/privacy',
  },
  'autoraja-delete-data': {
    title: 'AutoRaja Data Deletion | MixOut Technologies',
    description:
      'Request deletion of data associated with your use of AutoRaja, a mobile game by Mixout Technologies.',
    path: '/autoraja/delete-data',
  },
};

const normalizePath = (path) => {
  if (!path) {
    return '/';
  }

  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }

  return path;
};

const resolveRoute = (path) => {
  const normalized = normalizePath(path).toLowerCase();

  if (
    normalized === '/autoraja/credits' ||
    normalized === '/credits' ||
    normalized === '/licenses'
  ) {
    return 'autoraja-credits';
  }

  if (normalized === '/autoraja') {
    return 'autoraja';
  }

  if (normalized === '/autoraja/support') {
    return 'autoraja-support';
  }

  if (normalized === '/autoraja/privacy') {
    return 'autoraja-privacy';
  }

  if (normalized === '/autoraja/delete-data') {
    return 'autoraja-delete-data';
  }

  return 'home';
};

const upsertMetaTag = (selector, attributes) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    Object.entries(attributes.identifier).forEach(([key, value]) => {
      tag.setAttribute(key, value);
    });
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', attributes.content);
};

const upsertLinkTag = (rel, href) => {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
};

function App() {
  const [pathname, setPathname] = useState(normalizePath(window.location.pathname || '/'));
  const [pendingSection, setPendingSection] = useState(null);

  const route = useMemo(() => resolveRoute(pathname), [pathname]);

  const navigateToPath = (path) => {
    const normalized = normalizePath(path);
    if (normalized === pathname) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    window.history.pushState({}, '', normalized);
    setPathname(normalized);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const goToHomeSection = (sectionId) => {
    if (route === 'home') {
      scrollToSection(sectionId);
      return;
    }

    setPendingSection(sectionId);
    navigateToPath('/');
  };

  useEffect(() => {
    const handlePopState = () => {
      setPathname(normalizePath(window.location.pathname || '/'));
      window.scrollTo({ top: 0, behavior: 'auto' });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (route !== 'home' || !pendingSection) {
      return;
    }

    const handle = window.setTimeout(() => {
      scrollToSection(pendingSection);
      setPendingSection(null);
    }, 40);

    return () => window.clearTimeout(handle);
  }, [route, pendingSection]);

  useEffect(() => {
    const meta = ROUTE_META[route] || ROUTE_META.home;
    const url = `${SITE_ORIGIN}${meta.path}`;

    document.title = meta.title;

    upsertMetaTag('meta[name="description"]', {
      identifier: { name: 'description' },
      content: meta.description,
    });
    upsertMetaTag('meta[property="og:title"]', {
      identifier: { property: 'og:title' },
      content: meta.title,
    });
    upsertMetaTag('meta[property="og:description"]', {
      identifier: { property: 'og:description' },
      content: meta.description,
    });
    upsertMetaTag('meta[property="og:url"]', {
      identifier: { property: 'og:url' },
      content: url,
    });
    upsertMetaTag('meta[property="og:type"]', {
      identifier: { property: 'og:type' },
      content: 'website',
    });
    upsertMetaTag('meta[property="og:image"]', {
      identifier: { property: 'og:image' },
      content: `${SITE_ORIGIN}${route === 'home' ? DEFAULT_OG_IMAGE : '/logos/marketing/logo_landscape_2400x1350_safe.png'}`,
    });
    upsertMetaTag('meta[name="twitter:card"]', {
      identifier: { name: 'twitter:card' },
      content: 'summary_large_image',
    });
    upsertMetaTag('meta[name="twitter:title"]', {
      identifier: { name: 'twitter:title' },
      content: meta.title,
    });
    upsertMetaTag('meta[name="twitter:description"]', {
      identifier: { name: 'twitter:description' },
      content: meta.description,
    });

    upsertLinkTag('canonical', url);
  }, [route]);

  const products = [
    { name: 'AutoRaja', category: 'PLAY / MOBILE GAME', className: 'raja', image: '/AutoRaja_FeatureGraphic_With_Title_1794x877.png', alt: 'AutoRaja auto-rickshaw driving game', headline: 'Auto-rickshaw driving game.', description: 'Take the wheel of an auto-rickshaw. Dodge traffic, collect coins, and make every run your own.', status: 'iOS & Android', href: '/autoraja' },
    { name: 'MacDelta', category: 'UNDERSTAND / MAC UTILITY', className: 'delta', image: '/products/macdelta.png', alt: 'MacDelta native Mac interface showing system change history', headline: 'System change history for your Mac.', description: 'See what changed on your Mac. Explore system changes, recent files, downloads, and optional clipboard history, stored locally.', status: 'Built for macOS', href: 'https://macdelta.app' },
    { name: 'Studio Carto', category: 'CREATE / MAPS & DATA', className: 'carto', image: '/products/studio-carto.png', alt: 'Studio Carto workspace with maps and data visualizations', headline: 'Maps and charts in your browser.', description: 'Create maps and charts from a built-in library or your own data. Use the AI assistant with your own DeepSeek API key. Free in your browser, with no sign-up. Save projects locally and export your work.', status: 'Free web app · Available now', href: 'https://app.studiocarto.com' },
  ];

  const renderHomePage = () => (
    <main className="content company-home" id="main">
      <section id="home" className="company-hero container">
        <div className="hero-topline"><span>MIXOUT TECHNOLOGIES PRIVATE LIMITED</span></div>
        <h1>We develop software products.</h1>
        <div className="hero-bottom"><p>Mobile games, Mac utilities, and tools for maps and data.</p><a href="#product" className="explore-link">Explore our products <span aria-hidden="true">↘</span></a></div>
      </section>
      <section id="product" className="portfolio-section container" aria-labelledby="products-title">
        <div className="portfolio-heading"><h2 id="products-title">Our products</h2></div>
        <div className="portfolio-grid">{products.map((product, index) => (
          <a className={`portfolio-card ${product.className}`} key={product.name} href={product.href} aria-label={`Explore ${product.name}`}>
            <div className="product-art"><img src={product.image} alt={product.alt} /><span className="product-index">0{index + 1}</span></div>
            <div className="portfolio-copy"><p className="product-category">{product.category}</p><h3>{product.name}</h3><p className="product-headline">{product.headline}</p><p className="product-description">{product.description}</p><p className="product-status"><span aria-hidden="true" />{product.status}</p>

            </div>
          </a>
        ))}</div>
      </section>
      <section id="about" className="studio-section container"><p className="company-label">THE STUDIO</p><div><h2>About Mixout</h2><p>Mixout Technologies Private Limited is an independent software company in India. Our work spans mobile games, native Mac software, and tools for exploring the world through data.</p></div></section>
      <section id="contact" className="company-contact container"><div><p className="company-label">CONTACT</p><h2>Contact us</h2><a className="contact-email" href="mailto:contact@mixout.in">contact@mixout.in <span aria-hidden="true">↗</span></a></div><div className="company-address"><strong>Mixout Technologies Private Limited</strong><p>3803 Bayview, Hiranandani Upscale,<br />Old Mahabalipuram Road, Egattur,<br />Chengalpattu District, Tamil Nadu 603103, India</p></div></section>
    </main>
  );

  return (
    <div className="App">
      <Navbar
        route={route}
        scrollToSection={scrollToSection}
        navigateToPath={navigateToPath}
        goToHomeSection={goToHomeSection}
      />

      {route === 'autoraja' && <AutoRajaLanding navigateToPath={navigateToPath} />}
      {route === 'autoraja-support' && <AutoRajaSupport navigateToPath={navigateToPath} />}
      {route === 'autoraja-privacy' && <AutoRajaPrivacy navigateToPath={navigateToPath} />}
      {route === 'autoraja-delete-data' && <AutoRajaDeleteData navigateToPath={navigateToPath} />}
      {route === 'autoraja-credits' && <AutoRajaCredits navigateToPath={navigateToPath} />}
      {route === 'home' && renderHomePage()}

      <Footer />
    </div>
  );
}

export default App;
